import React, { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Search, ChevronDown, X, Download, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, CHART_COLORS } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IntelligenceExplorer = ({ embedded = false }) => {
  const [hotJobs, setHotJobs] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [titleDetail, setTitleDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/intelligence/filters`).then(setFilterOptions).catch(() => {});
    loadHotJobs();
  }, []);

  const loadHotJobs = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('limit', '50');
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/hot-jobs?${params}`);
      setHotJobs(data);
    } catch {}
    setLoading(false);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value || undefined };
    if (!value) delete newFilters[key];
    setFilters(newFilters);
    loadHotJobs(newFilters);
  };

  const loadTitleDetail = async (title) => {
    setSelectedTitle(title);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/title/${encodeURIComponent(title)}`);
      setTitleDetail(data);
    } catch { setTitleDetail(null); }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    window.open(`${API_URL}/api/intelligence/export/csv?${params}`, '_blank');
  };

  const filtered = hotJobs.filter(j => !searchQuery || j.title.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <IntelligenceLayout title="Job Explorer" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]">Job Demand Explorer</h1>
            <p className="text-sm text-gray-500">Investigate specific roles, trends, and hot scores</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleExport} className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="export-csv-btn">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors" data-testid="toggle-filters-btn">
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} /> Filters
            </button>
          </div>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search job titles..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 transition-all" data-testid="job-search-input" />
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pb-2" data-testid="filters-panel">
            {[
              { key: 'state', label: 'State', options: filterOptions.states },
              { key: 'industry', label: 'Industry', options: filterOptions.industries },
              { key: 'location_type', label: 'Work Format', options: filterOptions.location_types },
              { key: 'source', label: 'Source', options: filterOptions.sources },
            ].map(f => (
              <select key={f.key} value={filters[f.key] || ''} onChange={e => handleFilterChange(f.key, e.target.value)}
                className="bg-white border border-gray-300 rounded-lg text-sm text-gray-700 px-3 py-2 focus:outline-none focus:border-[#D4AF37]">
                <option value="">{f.label}: All</option>
                {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
          </div>
        )}
      </header>

      <main className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="hot-jobs-table">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider">Hot Jobs Ranking</h3>
                <span className="text-xs text-gray-400">{filtered.length} titles</span>
              </div>
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="text-xs text-gray-500 uppercase">
                      <th className="text-left px-4 py-2.5">#</th>
                      <th className="text-left px-4 py-2.5">Title</th>
                      <th className="text-right px-4 py-2.5">Postings</th>
                      <th className="text-right px-4 py-2.5">Growth</th>
                      <th className="text-right px-4 py-2.5">Hot Score</th>
                      <th className="text-right px-4 py-2.5">Sources</th>
                      <th className="text-right px-4 py-2.5">States</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((job, i) => (
                      <tr key={job.title} onClick={() => loadTitleDetail(job.title)}
                        className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${selectedTitle === job.title ? 'bg-[#D4AF37]/10' : ''}`}>
                        <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                        <td className="px-4 py-3 text-[#0A1F44] font-medium">{job.title}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{job.postings.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex items-center gap-0.5 ${job.growth_pct > 0 ? 'text-emerald-600' : job.growth_pct < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                            {job.growth_pct > 0 ? <ArrowUpRight className="h-3 w-3" /> : job.growth_pct < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                            {Math.abs(job.growth_pct)}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-block w-10 text-center text-xs font-bold py-0.5 rounded ${
                            job.hot_score >= 80 ? 'bg-red-100 text-red-600' :
                            job.hot_score >= 60 ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>{job.hot_score}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500">{job.sources}</td>
                        <td className="px-4 py-3 text-right text-gray-500">{job.states}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            {selectedTitle && titleDetail ? (
              <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5 sticky top-4 shadow-sm" data-testid="title-detail-panel">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-[#0A1F44]">{titleDetail.title}</h3>
                    <p className="text-sm text-gray-500">{titleDetail.total_postings} total postings</p>
                  </div>
                  <button onClick={() => { setSelectedTitle(null); setTitleDetail(null); }} className="text-gray-400 hover:text-gray-700">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {titleDetail.monthly_trend.length > 1 && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-2">Monthly Trend</p>
                    <ResponsiveContainer width="100%" height={120}>
                      <LineChart data={titleDetail.monthly_trend}>
                        <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} width={30} />
                        <Tooltip content={({ active, payload }) => active && payload?.length ? (
                          <div className="bg-white border border-gray-200 rounded px-2 py-1 text-xs shadow-md">
                            <span className="text-[#D4AF37] font-semibold">{payload[0].value}</span>
                          </div>
                        ) : null} />
                        <Line type="monotone" dataKey="count" stroke="#D4AF37" strokeWidth={2} dot={{ fill: '#D4AF37', r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div>
                  <p className="text-xs text-gray-500 uppercase mb-2">Top Markets</p>
                  <div className="space-y-1.5">
                    {titleDetail.by_state.slice(0, 6).map(s => (
                      <div key={s.state} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{s.state || 'Unknown'}</span>
                        <span className="text-gray-500">{s.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase mb-2">Industries</p>
                  <div className="space-y-1.5">
                    {titleDetail.by_industry.slice(0, 5).map(ind => (
                      <div key={ind.industry} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{ind.industry}</span>
                        <span className="text-gray-500">{ind.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase mb-2">Work Format</p>
                  <div className="flex gap-2">
                    {titleDetail.by_location_type.map(t => (
                      <span key={t.type} className="px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs text-gray-600">
                        {t.type}: {t.count}
                      </span>
                    ))}
                  </div>
                </div>

                {titleDetail.top_companies.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-2">Top Hiring Companies</p>
                    <div className="space-y-1.5">
                      {titleDetail.top_companies.slice(0, 6).map(c => (
                        <div key={c.company} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 truncate">{c.company}</span>
                          <span className="text-gray-500 flex-shrink-0 ml-2">{c.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center shadow-sm">
                <Search className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Click on a job title to see detailed analytics</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </IntelligenceLayout>
  );
};

export default IntelligenceExplorer;
